// src/modules/units/tyres/shared/tyre-sort.js
//
// Sorting tyres by a registry column. The sort key is the column's value() — the
// same machine-readable datum the CSV writes — so a column sorts by what it MEANS,
// not by the display string (distance sorts 9 before 12345, not "12 345 km"
// before "9 km"). Derived columns sort too, because value() computes them.
//
// A column's type is read from the css class the registry already carries, so the
// registry needs no new field: tm-col-num sorts numerically, tm-col-updated as a
// date, everything else as text (locale-aware, numeric-savvy so "AB2" precedes
// "AB10"). If a column ever needs to override this, it can declare `sortType`.
//
// Two rules that make the ordering feel right:
//   - Blanks always sink to the bottom, in BOTH directions. A missing tread or a
//     tyre with no yard is "unknown", and unknown is never what you're looking for
//     at the top of a sort.
//   - The natural first direction differs by type: numbers and dates default to
//     descending (highest / newest first — what you usually want), text to
//     ascending (A→Z). One click on "Highest temp" therefore puts the hottest on
//     top.

import { getColumn } from './tyre-columns.js';

const isBlank = (v) => v === null || v === undefined || v === '';

function kindOf(col) {
  if (col?.sortType) return col.sortType;          // explicit override, if ever set
  if (col?.cls === 'tm-col-num') return 'number';
  if (col?.cls === 'tm-col-updated') return 'date';
  return 'text';
}

/** The direction a column should take on its first click. */
export function defaultSortDir(key) {
  const k = kindOf(getColumn(key));
  return (k === 'number' || k === 'date') ? 'desc' : 'asc';
}

/** A comparator over tyre records for one column + direction. Blanks sort last. */
export function columnComparator(key, dir = 'asc') {
  const col = getColumn(key);
  if (!col) return () => 0;
  const kind = kindOf(col);
  const mul = dir === 'desc' ? -1 : 1;

  return (a, b) => {
    const va = col.value(a);
    const vb = col.value(b);
    const na = isBlank(va);
    const nb = isBlank(vb);
    if (na && nb) return 0;
    if (na) return 1;    // a blank -> after b, regardless of direction
    if (nb) return -1;   // b blank -> after a

    let cmp;
    if (kind === 'number') {
      cmp = Number(va) - Number(vb);
      if (Number.isNaN(cmp)) cmp = 0;
    } else if (kind === 'date') {
      cmp = new Date(va).getTime() - new Date(vb).getTime();
      if (Number.isNaN(cmp)) cmp = 0;
    } else {
      cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    }
    return cmp * mul;
  };
}

/** A new array of rows sorted by the column, or the same rows when key is falsy. */
export function sortTyres(rows, key, dir) {
  if (!key || !Array.isArray(rows)) return rows;
  return rows.slice().sort(columnComparator(key, dir));
}
